import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Card, Notification, useMediaQuery, Button, trackEvent } from '@offerzen/pinata'

import JobNotFound from '@/Jobs/components/JobNotFound'
import ApplicationCard from '@/Jobs/components/ApplicationCard'
import ApplyModal from '@/Jobs/components/ApplyModal'
import { userRole } from '@/CandidateExperience/selectors/currentUser'
import { UserRole } from '@/CandidateExperience/helpers/constants'
import { getCurrentCandidate } from '@/CandidateExperience/selectors/candidate'
import Toast, { useToast } from '@/CandidateExperience/components/Toast'

import { getPositionPreview, createApplication } from './api'
import JobDetailsSkeleton from './JobDetailsSkeleton'
import PositionPreview from './PositionPreview'
import styles from './styles'

const MobileSpacingWrapper = ({ children }) => {
  return <div className={styles.mobileSpacingWrapper}>{children}</div>
}

const JobDetails = () => {
  const [showModal, setShowModal] = useState(false)
  const toggleModal = () => setShowModal(!showModal)
  const { candidateSlug, jobId } = useParams()
  const { id: candidateProfileId } = useSelector(getCurrentCandidate)
  const { isTabletOrLarger } = useMediaQuery()

  const { data, isLoading, isError, error } = useQuery(['positionPreview', jobId, candidateProfileId], () => {
    return getPositionPreview({ positionId: jobId, candidateProfileId })
  })

  const queryClient = useQueryClient()

  const jobName = data?.position?.name
  const companyName = data?.position?.company?.name
  const loggedInUserRole = useSelector(userRole)

  useEffect(() => {
    if (jobName) {
      trackEvent('Candidate: position action', {
        action: 'viewed position',
        // TODO: Update this to cater for other sources like Applications
        source: 'candidate positions recommendations',
        position_name: jobName,
        position_id: jobId,
        candidate_id: candidateProfileId,
        company_name: companyName,
      })
    }
  }, [jobName, companyName, candidateProfileId, jobId])

  const { visibleToast, showToast, hideToast } = useToast()

  const {
    data: submittedApplication,
    mutate: submitApplication,
    isLoading: isSubmittingApplication,
    isSuccess: isApplicationSubmitted,
    isError: isApplicationError,
  } = useMutation('createApplication', () => createApplication({ positionId: jobId, candidateProfileId }), {
    onSuccess: () => {
      toggleModal()
      showToast({ theme: 'success', message: 'Application sent!' })

      queryClient.invalidateQueries({
        queryKey: ['positionPreview', jobId, candidateProfileId],
        exact: true,
      })
    },
  })

  if (isError && error?.message?.includes('not_found')) {
    return (
      <MobileSpacingWrapper>
        <JobNotFound />
      </MobileSpacingWrapper>
    )
  }

  if (isError) {
    return (
      <MobileSpacingWrapper>
        <Notification theme="error">
          <div role="alert">Something went wrong.</div>
        </Notification>
      </MobileSpacingWrapper>
    )
  }

  if (isLoading || !data) {
    return (
      <MobileSpacingWrapper>
        <JobDetailsSkeleton />
      </MobileSpacingWrapper>
    )
  }

  const { position } = data
  const application = isApplicationSubmitted ? submittedApplication.createApplication.application : position.application

  return (
    <>
      <Toast theme={visibleToast?.theme} message={visibleToast?.message} onDismiss={hideToast} />

      <div className={styles.container}>
        <Card className={styles.jobDetails}>
          <PositionPreview position={position}>
            <If condition={!isTabletOrLarger}>
              <aside>
                <ApplicationCard
                  className={styles.applicationCardMobile}
                  theme="light"
                  jobId={jobId}
                  candidateSlug={candidateSlug}
                  candidateProfileId={candidateProfileId}
                  application={application}
                  handleApply={toggleModal}
                  loggedInUserRole={loggedInUserRole}
                />
              </aside>
            </If>
          </PositionPreview>
        </Card>

        <If condition={isTabletOrLarger}>
          <aside>
            <ApplicationCard
              className={styles.applicationCardTablet}
              jobId={jobId}
              candidateSlug={candidateSlug}
              candidateProfileId={candidateProfileId}
              application={application}
              handleApply={toggleModal}
              loggedInUserRole={loggedInUserRole}
            />
          </aside>
        </If>

        <ApplyModal
          showModal={showModal}
          hideModal={toggleModal}
          positionName={position.name}
          companyName={position.company?.name}
          submitApplication={submitApplication}
          isSubmittingApplication={isSubmittingApplication}
          isApplicationError={isApplicationError}
        />
      </div>
    </>
  )
}

MobileSpacingWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
}

export default JobDetails
